import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check current session on mount
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error fetching session:', error);
        } else {
          console.log('Initial session:', session);
          setUser(session?.user ?? null);
        }
      } catch (err) {
        console.error('Unexpected error fetching session:', err);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', { event, session });
      
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
      }
      
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        setUser(null);
      } else if (session) {
        setUser(session.user);
      }
      
      setLoading(false);
    });

    // Proactively refresh the session when the user returns to the tab or window.
    // This is the most reliable fix for idle-tab JWT expiry because browser timers
    // are throttled on hidden/inactive tabs, so we can't rely solely on autoRefreshToken.
    const proactiveRefresh = async () => {
      try {
        const { error } = await supabase.auth.refreshSession();
        if (error) {
          console.warn('Proactive session refresh failed:', error.message);
        } else {
          console.log('Session proactively refreshed on tab/window focus');
        }
      } catch (err) {
        console.warn('Unexpected error during proactive refresh:', err);
      }
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') proactiveRefresh();
    });
    window.addEventListener('focus', proactiveRefresh);

    // Periodic background refresh every 10 minutes to keep token alive even while
    // the user is working on the same page (replaces the broken getSession check).
    const refreshInterval = setInterval(async () => {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn('Periodic session refresh failed:', error.message);
      } else {
        console.log('Periodic session refresh succeeded');
      }
    }, 10 * 60 * 1000); // 10 minutes

    // Cleanup listener and interval on unmount
    return () => {
      authListener.subscription.unsubscribe();
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', proactiveRefresh);
      window.removeEventListener('focus', proactiveRefresh);
    };
  }, []);

  const login = async (email, password) => {
    // Validate inputs
    if (typeof email !== 'string' || typeof password !== 'string') {
      console.error('Invalid input types:', { email, password });
      throw new Error('Email et mot de passe doivent être des chaînes de caractères');
    }

    console.log('Attempting login with:', { email, password });

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        console.error('Supabase login error:', error.message);
        throw new Error(error.message);
      }

      console.log('Login successful, user:', data.user);
      setUser(data.user);
      return data.user;
    } catch (err) {
      console.error('Unexpected login error:', err);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      console.log('Logged out successfully');
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);